import { inject, Pipe, PipeTransform } from '@angular/core';
import { PermissionService } from '../services/permission.service';

/**
 * Pipe to check permissions in templates
 *
 * Usage examples:
 *
 * // Single permission
 * <button [disabled]="!('campaign_create' | hasPermission)">Create Campaign</button>
 *
 * // Multiple permissions (ANY by default)
 * <div *ngIf="['campaign_create', 'campaign_update'] | hasPermission">Campaign Actions</div>
 *
 * // Multiple permissions (ALL required)
 * <div *ngIf="['admin_access', 'super_admin'] | hasPermission:true">Super Admin Panel</div>
 *
 * // Role checking
 * <div *ngIf="'admin' | hasRole">Admin Content</div>
 *
 * // Negation
 * <div *ngIf="!('admin_access' | hasPermission)">Non-admin content</div>
 */
@Pipe({
  name: 'hasPermission',
  pure: false, // Not pure because permissions can change during the component lifecycle
})
export class HasPermissionPipe implements PipeTransform {
  private readonly permissionService = inject(PermissionService);
  /**
   * Transform method to check permissions
   * @param permissions - Single permission string or array of permissions
   * @param requireAll - If true, requires ALL permissions. If false (default), requires ANY permission
   * @returns boolean indicating if user has the required permissions
   */
  public transform(permissions: string | string[] | null, requireAll = false): boolean {
    if (!permissions) {
      return true;
    }

    const permissionArray = Array.isArray(permissions) ? permissions : [permissions];

    if (requireAll) {
      return this.permissionService.hasAllPermissions(permissionArray);
    } else {
      return this.permissionService.hasAnyPermission(permissionArray);
    }
  }
}

/**
 * Pipe to check roles in templates
 *
 * Usage examples:
 *
 * // Single role
 * <div *ngIf="'admin' | hasRole">Admin Content</div>
 *
 * // Multiple roles (ANY by default)
 * <div *ngIf="['admin', 'campaign'] | hasRole">Admin or Campaign Manager Content</div>
 *
 * // Multiple roles (ALL required)
 * <div *ngIf="['admin', 'super_admin'] | hasRole:true">Super Admin Content</div>
 */
@Pipe({
  name: 'hasRole',
  pure: false,
})
export class HasRolePipe implements PipeTransform {
  private readonly permissionService = inject(PermissionService);

  /**
   * Transform method to check roles
   * @param roles - Single role string or array of roles
   * @param requireAll - If true, requires ALL roles. If false (default), requires ANY role
   * @returns boolean indicating if user has the required roles
   */
  public transform(roles: string | string[] | null, requireAll = false): boolean {
    if (!roles) {
      return true;
    }

    const roleArray = Array.isArray(roles) ? roles : [roles];

    if (requireAll) {
      const userRoles = this.permissionService.getCurrentUserRoles();

      return roleArray.every((role) => userRoles.includes(role));
    } else {
      return this.permissionService.hasAnyRole(roleArray);
    }
  }
}

/**
 * Pipe to get current user's permissions
 *
 * Usage examples:
 *
 * // Display user's permissions
 * <div *ngFor="let permission of (null | userPermissions)">{{ permission }}</div>
 *
 * // Check if user has specific permission from a dynamic list
 * <div *ngIf="(null | userPermissions).includes('campaign_create')">Can create campaigns</div>
 */
@Pipe({
  name: 'userPermissions',
  pure: false,
})
export class UserPermissionsPipe implements PipeTransform {
  private readonly permissionService = inject(PermissionService);

  public transform(): string[] {
    return this.permissionService.currentUserPermissions;
  }
}

/**
 * Pipe to get current user's roles
 *
 * Usage examples:
 *
 * // Display user's roles
 * <div *ngFor="let role of (null | userRoles)">{{ role }}</div>
 *
 * // Check if user has specific role from a dynamic list
 * <div *ngIf="(null | userRoles).includes('admin')">User is admin</div>
 */
@Pipe({
  name: 'userRoles',
  pure: false,
})
export class UserRolesPipe implements PipeTransform {
  private readonly permissionService = inject(PermissionService);

  public transform(): string[] {
    return this.permissionService.getCurrentUserRoles();
  }
}
